import {
  PermissionCode,
  TICKET_PRIORITY_LABELS,
  type TicketAiSuggestionAction,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { Loader2Icon, SparklesIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { ApiRequestError } from '@/lib/api';
import { AiMarkdown } from '@/platform/components/ai-markdown';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import {
  useAcceptTicketAiSuggestion,
  useRejectTicketAiSuggestion,
} from '@/products/helpdesk/features/tickets/use-tickets';

function confidenceLabel(value: number): string {
  return `${Math.round(value * 100)} %`;
}

export function TicketAiPanel({ ticket }: { ticket: TicketDetail }) {
  const me = useMe();
  const canConfirm = hasPermission(me.data, PermissionCode.TICKETS_UPDATE);
  const accept = useAcceptTicketAiSuggestion(String(ticket.number));
  const reject = useRejectTicketAiSuggestion(String(ticket.number));
  const suggestion = ticket.ai.suggestion;
  const pending = Boolean(suggestion && (suggestion.pendingCategory || suggestion.pendingPriority));

  async function decide(kind: 'accept' | 'reject', fields?: TicketAiSuggestionAction['fields']) {
    const body: TicketAiSuggestionAction = fields ? { fields } : {};
    try {
      if (kind === 'accept') {
        await accept.mutateAsync(body);
        toast.success('KI-Vorschlag übernommen.');
      } else {
        await reject.mutateAsync(body);
        toast.success('KI-Vorschlag verworfen.');
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Entscheidung konnte nicht gespeichert werden.',
      );
    }
  }

  if (!pending || !suggestion) {
    return null;
  }

  const busy = accept.isPending || reject.isPending;
  const categoryLabel = suggestion.category
    ? `${suggestion.category.parentName ? `${suggestion.category.parentName} / ` : ''}${suggestion.category.name}`
    : null;

  return (
    <TicketSectionCard
      tone="navy"
      icon={SparklesIcon}
      collapsible
      defaultOpen={pending}
      title="KI-Vorschlag"
      description="Bitte prüfen, bevor der Wert übernommen wird."
    >
      <div className="space-y-3 text-sm">
        {suggestion.pendingCategory && categoryLabel && (
          <p>
            <span className="text-muted-foreground">Kategorie: </span>
            {categoryLabel}
            <span className="text-muted-foreground">
              {' '}
              · {confidenceLabel(suggestion.categoryConfidence)}
            </span>
          </p>
        )}
        {suggestion.pendingPriority && suggestion.priority && (
          <p>
            <span className="text-muted-foreground">Priorität: </span>
            {TICKET_PRIORITY_LABELS[suggestion.priority]}
            <span className="text-muted-foreground">
              {' '}
              · {confidenceLabel(suggestion.priorityConfidence)}
            </span>
          </p>
        )}
        {suggestion.rationale ? (
          <AiMarkdown text={suggestion.rationale} className="text-muted-foreground" />
        ) : null}
        {canConfirm && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={busy} onClick={() => void decide('accept')}>
              {accept.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Übernehmen
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void decide('reject')}
            >
              Verwerfen
            </Button>
          </div>
        )}
      </div>
    </TicketSectionCard>
  );
}

export function AiSetBadge() {
  return (
    <Badge variant="secondary" className="font-normal">
      Von KI gesetzt
    </Badge>
  );
}
